/**
 * LES REGLES D'AFFICHAGE DE L'INDICATEUR D'ENREGISTREMENT d'une fiche.
 *
 * Machine a etats PURE : ni React, ni minuteur, ni horloge. Le chemin
 * d'ecriture (`commitWrite`, `views/useScreen.ts`) ne bouge pas et continue de
 * publier `saveState` a chaque ecriture ; ce fichier ne decide que de ce que la
 * rangee 3 MONTRE, pour qu'une rafale de modifications ne clignote pas.
 *
 * Le BUT : trois modifications en cinq secondes donnent UN SEUL « Enregistre ».
 *
 * L'etat porte trois choses : la phase, le contenu montre (meme pendant la
 * sortie, pour que le fondu montre encore le mot) et l'ECHEANCE du prochain
 * minuteur (`null` : aucun). Le hook (`useIndicateurEnregistrement`, dans
 * `useIndicateurEnregistrement.ts`) ne fait que poser UN `setTimeout` sur
 * l'echeance et envoyer `'minuteur'` quand elle tombe.
 *
 * Les phases :
 * - `cache`          : rien de montre, rien en vol.
 * - `attenteVol`     : une ecriture est en vol, rien de montre ; passe en
 *                      `enCours` si le vol dure.
 * - `enCours`        : « Enregistrement... » (spinner), jusqu'a la reponse.
 * - `calme`          : l'ecriture a repondu, rien de montre ; on attend que
 *                      plus rien n'arrive avant de dire « Enregistre ».
 * - `enregistre`     : « Enregistre » montre, tenue en cours.
 * - `enregistreEnVol`: « Enregistre » reste montre (pas de clignotement)
 *                      pendant qu'une nouvelle ecriture est en vol ; ses
 *                      minuteurs de tenue sont suspendus.
 * - `sortie`         : le fondu de disparition (le mot reste le temps du fondu).
 * - `erreur`         : l'echec, rendu dans le flux par `SaveIndicator`, jamais
 *                      par la surimpression : ici il ne montre rien.
 */

/** Les delais, en millisecondes. Gelee : un delai se discute, il ne se mute pas. */
export const DELAIS_INDICATEUR = Object.freeze({
  /** « Enregistrement... » n'apparait que si le vol dure plus que ca. */
  enCoursApres: 1000,
  /** Calme exige avant « Enregistre » : une nouvelle ecriture l'annule. */
  calmeAvantEnregistre: 1500,
  /** Duree pendant laquelle « Enregistre » reste montre. */
  tenue: 3000,
  /** Le fondu de sortie : la duree de l'animation CSS (`--dur-slow`, `styles/tokens.css`). */
  sortie: 320,
});

export type PhaseIndicateur =
  | 'cache'
  | 'attenteVol'
  | 'enCours'
  | 'calme'
  | 'enregistre'
  | 'enregistreEnVol'
  | 'sortie'
  | 'erreur';

/** Ce que la surimpression montre : un mot, ou rien. */
export type ContenuIndicateur = 'enCours' | 'enregistre' | null;

export interface EtatIndicateur {
  phase: PhaseIndicateur;
  /** Le mot montre, y compris pendant le fondu de sortie. */
  contenu: ContenuIndicateur;
  /** L'instant (ms) du prochain minuteur, ou `null` quand rien n'est attendu. */
  echeance: number | null;
}

/** Les quatre modes de `SaveState`, plus la chute d'un minuteur. */
export type EvenementIndicateur = 'idle' | 'saving' | 'saved' | 'error' | 'minuteur';

export const ETAT_INDICATEUR_INITIAL: EtatIndicateur = Object.freeze({
  phase: 'cache',
  contenu: null,
  echeance: null,
});

/** La surimpression est-elle visible (opacite 1) ? Faux pendant le fondu de sortie. */
export function indicateurVisible(etat: EtatIndicateur): boolean {
  return etat.phase === 'enCours' || etat.phase === 'enregistre' || etat.phase === 'enregistreEnVol';
}

/**
 * Le reducteur. `maintenant` est l'instant de l'evenement, en millisecondes ;
 * il n'est jamais lu ailleurs que dans cet argument.
 */
export function reduireIndicateur(
  etat: EtatIndicateur,
  evenement: EvenementIndicateur,
  maintenant: number,
): EtatIndicateur {
  switch (evenement) {
    case 'idle':
      // Un changement de fiche ou de session : tout disparait, sans fondu.
      return etat.phase === 'cache' && etat.contenu === null && etat.echeance === null
        ? etat
        : ETAT_INDICATEUR_INITIAL;

    case 'error':
      // L'echec est dit tout de suite et reste : aucun minuteur.
      return { phase: 'erreur', contenu: null, echeance: null };

    case 'saving':
      switch (etat.phase) {
        case 'attenteVol':
        case 'enCours':
        case 'enregistreEnVol':
          // Un vol est deja suivi : le plus ancien compte.
          return etat;
        case 'enregistre':
          // « Enregistre » reste (pas de clignotement), sa tenue est suspendue.
          return {
            phase: 'enregistreEnVol',
            contenu: 'enregistre',
            echeance: maintenant + DELAIS_INDICATEUR.enCoursApres,
          };
        default:
          // cache, calme (le calme est annule), sortie (le fondu est coupe), erreur.
          return {
            phase: 'attenteVol',
            contenu: null,
            echeance: maintenant + DELAIS_INDICATEUR.enCoursApres,
          };
      }

    case 'saved':
      switch (etat.phase) {
        case 'enCours':
        case 'enregistre':
        case 'enregistreEnVol':
          // « Enregistrement... » ou « Enregistre » etait deja la : le mot
          // juste, tout de suite, et la tenue repart de maintenant.
          return {
            phase: 'enregistre',
            contenu: 'enregistre',
            echeance: maintenant + DELAIS_INDICATEUR.tenue,
          };
        default:
          // Rien de montre : on attend le calme, une nouvelle ecriture l'annule.
          return {
            phase: 'calme',
            contenu: null,
            echeance: maintenant + DELAIS_INDICATEUR.calmeAvantEnregistre,
          };
      }

    case 'minuteur': {
      if (etat.echeance === null || maintenant < etat.echeance) return etat;
      switch (etat.phase) {
        case 'attenteVol':
        case 'enregistreEnVol':
          return { phase: 'enCours', contenu: 'enCours', echeance: null };
        case 'calme':
          return {
            phase: 'enregistre',
            contenu: 'enregistre',
            echeance: maintenant + DELAIS_INDICATEUR.tenue,
          };
        case 'enregistre':
          return {
            phase: 'sortie',
            contenu: 'enregistre',
            echeance: maintenant + DELAIS_INDICATEUR.sortie,
          };
        case 'sortie':
          return ETAT_INDICATEUR_INITIAL;
        default:
          return etat;
      }
    }
  }
}
